import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import { defaultLocale, polishLocale } from "../constants/countries"
import { LocalStorage } from "../constants/local-storage"
import { useLocalizationContext } from "../context/localization.context"
import { detectCurrentLocation } from "../helpers/get-client-locale"
import {
  getFromLocalStorage,
  setInLocalStorage,
} from "./use-state-with-local-storage.hook"
import pl from "../images/icons/countries/pl1.svg"
import us from "../images/icons/countries/us1.svg"

import notFound from "../../locales/en/404.json"
import confirmation from "../../locales/en/confirmation.json"
import account from "../../locales/en/account.json"
import careerOffer from "../../locales/en/career-offer.json"
import common from "../../locales/en/common.json"
import copyrights from "../../locales/en/copyrights.json"
import mediaLibrary from "../../locales/en/media-library.json"
import newsletter from "../../locales/en/newsletter.json"
import pressReleases from "../../locales/en/press-releases.json"
import products from "../../locales/en/products.json"
import sarChecker from "../../locales/en/sar-checker.json"
import innovationLab from "../../locales/en/innovation-lab.json"
import Cookies from "js-cookie"
import { Cookie } from "../constants/cookies"

export const kompaktAdditionalLanguages = ["de", "es", "fr", "it"]

export const englishLanguage = {
  label: "ENGLISH",
  image: {
    src: us,
    alt: "",
  },
  value: "en",
}

const polishLanguage = {
  label: "POLISH",
  image: {
    src: pl,
    alt: "",
  },
  value: "pl",
}

const translatedResources = {
  ...confirmation,
  ...notFound,
  ...account,
  ...careerOffer,
  ...common,
  ...copyrights,
  ...mediaLibrary,
  ...newsletter,
  ...pressReleases,
  ...products,
  ...sarChecker,
  ...innovationLab,
}

const translationOptions = [englishLanguage, polishLanguage]

const validTranslationResolver = (
  country: string,
  languagesArray: string[]
): string => {
  const language: string | undefined = languagesArray.filter(
    (lang) => lang === country.toLowerCase()
  )[0]

  return language ?? defaultLocale.value
}

const localeResolver = (country: string | null) => {
  if (country?.includes(polishLocale.value)) {
    return polishLocale.locale
  } else {
    return defaultLocale.locale
  }
}

const pathResolver = (country: string | null) => {
  if (country?.includes(polishLocale.value)) {
    return polishLocale.pathPrefix
  } else {
    return defaultLocale.pathPrefix
  }
}

const setCountryCookie = (country: string): void => {
  const cookieData = Cookies.get(Cookie.Country)
  if (!cookieData || cookieData !== country) {
    Cookies.set(Cookie.Country, country, {
      expires: 365,
      domain: ".mudita.com",
      path: "/",
    })
  }
}

const useTranslationHook = () => {
  const { setLocale, setPathPrefix } = useLocalizationContext()
  const { changeLanguage, language, languages, originalPath } = useI18next()
  const { t } = useTranslation()

  const setAllLocaleVars = (
    country: string | null,
    languages: string[]
  ): void => {
    if (!country) {
      return
    }
    let currentPath = originalPath

    if (location && location.hash) {
      currentPath += location.hash
    }

    setLocale(localeResolver(country))
    setPathPrefix(pathResolver(country))
    setInLocalStorage(LocalStorage.PrefferedLang)(country)
    setCountryCookie(country.toUpperCase())
    changeLanguage(validTranslationResolver(country, languages), currentPath, {
      replace: false,
    })
  }

  const fetchAndSaveCountry = async () => {
    const clientPrefferedLang = getFromLocalStorage(LocalStorage.PrefferedLang)
    if (!clientPrefferedLang) {
      const clientCountry = await detectCurrentLocation()
      setAllLocaleVars(clientCountry, languages)
    } else {
      setAllLocaleVars(clientPrefferedLang, languages)
    }
  }
  const changeLanguageManually = (country: string) => {
    setAllLocaleVars(country, languages)
  }

  const translate = (key: keyof typeof translatedResources): string => {
    return t(key)
  }

  const dynamicTranslate = (key: string): string => {
    return t(key)
  }
  return {
    language,
    translationOptions,
    changeLanguageManually,
    fetchAndSaveCountry,
    translate,
    dynamicTranslate,
  }
}

export default useTranslationHook
